// C:\laravel\Php_ASM_Laravel_React\frontend\src\pages\ProfileSettings.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Sidebar from '../components/ui/Sidebar';
import Breadcrumb from '../components/ui/Breadcrumb';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import axiosInstance from "../utils/axios";

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  
  // Profile form state - REMOVED: department, jobTitle, manager
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    employeeId: '',
    // department: '', // REMOVED
    // jobTitle: '', // REMOVED
    phoneNumber: '',
    address: '',
    // manager: '', // REMOVED
    timezone: 'UTC+0',
    language: 'English',
    dateFormat: 'MM/DD/YYYY',
    emailNotifications: true,
    pushNotifications: true,
    twoFactorAuth: false
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Form errors
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Timezone options
  const timezoneOptions = [
    { value: 'UTC-12', label: 'UTC-12:00' },
    { value: 'UTC-11', label: 'UTC-11:00' },
    { value: 'UTC-10', label: 'UTC-10:00' },
    { value: 'UTC-9', label: 'UTC-09:00' },
    { value: 'UTC-8', label: 'UTC-08:00' },
    { value: 'UTC-7', label: 'UTC-07:00' },
    { value: 'UTC-6', label: 'UTC-06:00' },
    { value: 'UTC-5', label: 'UTC-05:00' },
    { value: 'UTC-4', label: 'UTC-04:00' },
    { value: 'UTC-3', label: 'UTC-03:00' },
    { value: 'UTC-2', label: 'UTC-02:00' },
    { value: 'UTC-1', label: 'UTC-01:00' },
    { value: 'UTC+0', label: 'UTC+00:00' },
    { value: 'UTC+1', label: 'UTC+01:00' },
    { value: 'UTC+2', label: 'UTC+02:00' },
    { value: 'UTC+3', label: 'UTC+03:00' },
    { value: 'UTC+4', label: 'UTC+04:00' },
    { value: 'UTC+5', label: 'UTC+05:00' },
    { value: 'UTC+6', label: 'UTC+06:00' },
    { value: 'UTC+7', label: 'UTC+07:00' },
    { value: 'UTC+8', label: 'UTC+08:00' },
    { value: 'UTC+9', label: 'UTC+09:00' },
    { value: 'UTC+10', label: 'UTC+10:00' },
    { value: 'UTC+11', label: 'UTC+11:00' },
    { value: 'UTC+12', label: 'UTC+12:00' }
  ];

  // Language options
  const languageOptions = [
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Español' },
    { value: 'French', label: 'Français' },
    { value: 'German', label: 'Deutsch' },
    { value: 'Chinese', label: '中文' },
    { value: 'Japanese', label: '日本語' }
  ];

  // Date format options
  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
  ];

  // Load user data
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);

      const res = await axiosInstance.get("/me");

      const user = res.data.user;

      setProfile({
        name: user.name || "",
        email: user.email || "",
        employeeId: user.employee_id || "",
        // department: user.department || "", // REMOVED
        // jobTitle: user.job_title || user.title || "", // REMOVED
        phoneNumber: user.phone_number || "",
        address: user.address || user.location || "",
        // manager: user.manager || "", // REMOVED
        timezone: user.timezone || "UTC+0",
        language: user.language || "English",
        dateFormat: user.date_format || "MM/DD/YYYY",
        emailNotifications: true,
        pushNotifications: true,
        twoFactorAuth: false,
      });

      // profile image preview
      if (user.profile_image) {
        setAvatarPreview(user.profile_image_url || user.profile_image);
      }

    } catch (error) {
      console.log("Profile fetch error:", error);

    } finally {
      setIsLoading(false);
    }
  };

  // Handle profile input changes
  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // preview instantly
    const tempUrl = URL.createObjectURL(file);
    setAvatarPreview(tempUrl);

    const formData = new FormData();
    formData.append("profile_image", file);

    try {
      setIsSaving(true);

      const res = await axiosInstance.post(
        "/profile/upload-image",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      const uploadedPath = res.data.profile_image;

      setAvatarPreview(
        res.data.profile_image_url || res.data.profile_image
      );

      setProfile(prev => ({
        ...prev,
        profile_image: uploadedPath
      }));

      const updatedUser = {
        ...JSON.parse(sessionStorage.getItem("user") || "{}"),
        profile_image: uploadedPath,
        profile_image_url:
          res.data.profile_image_url || res.data.profile_image
      };

      sessionStorage.setItem(
        "user",
        JSON.stringify(updatedUser)
      );

      window.dispatchEvent(new Event("userUpdated"));

      setSuccessMessage("Image uploaded successfully");

    } catch (err) {
      console.log(err);
      setErrors({ api: "Image upload failed" });
    } finally {
      setIsSaving(false);
    }
  };

  // Validate profile form
  const validateProfile = () => {
    const newErrors = {};
    if (!profile.name.trim()) newErrors.name = 'Name is required';
    if (!profile.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(profile.email)) newErrors.email = 'Email is invalid';
    if (!profile.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    return newErrors;
  };

  // Handle profile save - REMOVED department, jobTitle, manager
  const handleSaveProfile = async () => {
    const newErrors = validateProfile();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setErrors({});

    try {
      const formData = new FormData();

      formData.append("name", profile.name);
      formData.append("email", profile.email);
      // formData.append("department", profile.department); // REMOVED
      // formData.append("job_title", profile.jobTitle); // REMOVED
      formData.append("phone_number", profile.phoneNumber);
      formData.append("address", profile.address);
      // formData.append("manager", profile.manager); // REMOVED
      
      if (profile.profile_image) {
        formData.append("profile_image", profile.profile_image);
      }

      const res = await axiosInstance.post("/profile/update", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("UPDATE RESPONSE:", res.data);

      window.alert(res?.data?.message || "Profile updated successfully");
      setSuccessMessage(res?.data?.message || "Profile updated successfully");
      setErrors({});

      // update UI instantly
      setProfile(prev => ({
        ...prev,
        ...res.data.user,
        phoneNumber: res.data.user.phone_number,
        employeeId: res.data.user.employee_id,
        address: res.data.user.address || res.data.user.location,
      }));

      sessionStorage.setItem("user", JSON.stringify(res.data.user));

      window.dispatchEvent(new Event("userUpdated"));

    } catch (error) {
      console.log(error);

      const response = error.response?.data;

      if (response?.errors) {
        setErrors(response.errors);
      }

      if (response?.message) {
        setErrors(prev => ({
          ...prev,
          api: response.message
        }));
      }

      if (!response) {
        setErrors({ api: "Server error occurred" });
      }

    } finally {
      setIsSaving(false);
    }
  };

  // Validate password
  const validatePassword = () => {
    const newErrors = {};
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    return newErrors;
  };

  // Handle password change
  const handleChangePassword = () => {
    const newErrors = validatePassword();
    if (Object.keys(newErrors).length > 0) {
      setPasswordErrors(newErrors);
      return;
    }

    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      setSuccessMessage('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      setPasswordErrors({});
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsSaving(false);
    }, 500);
  };

  const tabs = [
    { id: 'profile', label: 'Profile Information', icon: 'User' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Profile Settings</h1>
            <p className="text-muted-foreground mt-2">
              Manage your personal information, security settings, and preferences
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 flex items-start justify-between bg-green-50 border border-green-200 text-green-800 rounded-lg shadow-sm">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={18} className="text-green-600" />
                <span className="text-sm font-medium">
                  {successMessage}
                </span>
              </div>
              <button
                onClick={() => setSuccessMessage('')}
                className="text-green-600 hover:text-green-800"
              >
                ✕
              </button>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Profile Information Tab */}
              {activeTab === 'profile' && (
                <div className="max-w-4xl">
                  {/* Avatar Section */}
                  <div className="bg-card border border-border rounded-lg p-6 mb-6">
                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center overflow-hidden">
                          {avatarPreview ? (
                            <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-primary-foreground font-semibold text-2xl">
                              {profile.name?.split(' ')?.map(n => n?.[0])?.join('')}
                            </span>
                          )}
                        </div>
                        <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
                          <Icon name="Camera" size={14} className="text-primary-foreground" />
                          <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                        </label>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-card-foreground">{profile.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">Employee ID: {profile.employeeId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Profile Form */}
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-card-foreground mb-6">Personal Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Full Name <span className="text-error">*</span>
                        </label>
                        <Input
                          value={profile.name}
                          onChange={(e) => handleProfileChange('name', e.target.value)}
                          error={errors.name}
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Email Address <span className="text-error">*</span>
                        </label>
                        <Input
                          type="email"
                          value={profile.email}
                          onChange={(e) => handleProfileChange('email', e.target.value)}
                          error={errors.email}
                          placeholder="your.email@company.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Phone Number <span className="text-error">*</span>
                        </label>
                        <Input
                          value={profile.phoneNumber}
                          onChange={(e) => handleProfileChange('phoneNumber', e.target.value)}
                          error={errors.phoneNumber}
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Address
                        </label>
                        <Input
                          value={profile.address}
                          onChange={(e) => handleProfileChange('address', e.target.value)}
                          placeholder="Street, City, Country"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end mt-6 pt-6 border-t border-border">
                      <Button
                        variant="default"
                        onClick={handleSaveProfile}
                        isLoading={isSaving}
                        iconName="Save"
                        iconPosition="left"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && (
                <div className="max-w-4xl">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-card-foreground mb-6">App Preferences</h3>
                    
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-card-foreground mb-2">
                            Timezone
                          </label>
                          <Select
                            options={timezoneOptions}
                            value={profile.timezone}
                            onChange={(value) => handleProfileChange('timezone', value)}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-card-foreground mb-2">
                            Language
                          </label>
                          <Select
                            options={languageOptions}
                            value={profile.language}
                            onChange={(value) => handleProfileChange('language', value)}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-card-foreground mb-2">
                            Date Format
                          </label>
                          <Select
                            options={dateFormatOptions}
                            value={profile.dateFormat}
                            onChange={(value) => handleProfileChange('dateFormat', value)}
                          />
                        </div>
                      </div>

                      <div className="border-t border-border pt-6">
                        <h4 className="text-md font-semibold text-card-foreground mb-4">Notifications</h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-card-foreground">Email Notifications</p>
                              <p className="text-xs text-muted-foreground">Receive notifications via email</p>
                            </div>
                            <button
                              onClick={() => handleProfileChange('emailNotifications', !profile.emailNotifications)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                profile.emailNotifications ? 'bg-primary' : 'bg-muted'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  profile.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-card-foreground">Push Notifications</p>
                              <p className="text-xs text-muted-foreground">Receive push notifications in browser</p>
                            </div>
                            <button
                              onClick={() => handleProfileChange('pushNotifications', !profile.pushNotifications)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                profile.pushNotifications ? 'bg-primary' : 'bg-muted'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  profile.pushNotifications ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end mt-6 pt-6 border-t border-border">
                      <Button
                        variant="default"
                        onClick={handleSaveProfile}
                        isLoading={isSaving}
                        iconName="Save"
                        iconPosition="left"
                      >
                        Save Preferences
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={`bg-card border-t border-border py-4 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="px-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span>© {new Date()?.getFullYear()} AssetTracker Pro</span>
              <span>•</span>
              <span>Profile Settings</span>
            </div>
            <div className="flex items-center space-x-4">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
              <span>•</span>
              <span>Version 1.0.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfileSettings;